import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDown, CheckCircle2 } from 'lucide-react';
import { siteData } from '../mock/data';
import ScrollReveal, { ImageReveal, useInView } from '../components/ScrollReveal';
import ContactBlock from '../components/ContactBlock';
import Marquee from '../components/Marquee';
import CountUp from '../components/CountUp';

const Home = () => {
  const { home, services } = siteData;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="container-x relative z-10">
          <div className="hero-eyebrow font-mono-up text-[#0A2A28]/60 mb-10">
            <span className="gold-line" />{home.eyebrow}
          </div>
          <h1 className="font-display text-[14vw] md:text-[9.5vw] leading-[0.92] text-[#0A2A28] -tracking-[0.02em]">
            <span className="hero-title block">{home.title}</span>
            <span className="hero-title-2 block font-serif-italic text-[#BFA070]">{home.titleAccent}</span>
          </h1>

          <div className="grid md:grid-cols-12 gap-12 md:gap-16 mt-20 items-start">
            <div className="md:col-span-6 hero-para">
              <p className="text-[#0A2A28]/80 text-lg leading-relaxed mb-6">{home.intro}</p>
              <p className="text-[#0A2A28]/70 leading-relaxed">{home.intro2}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/leistungen" className="lux-btn">
                  <span>Leistungen entdecken</span>
                  <ArrowUpRight className="arrow" size={16} strokeWidth={1.5} />
                </Link>
              </div>
              <div className="mt-16 flex items-center gap-4 text-[11px] tracking-[0.28em] uppercase text-[#0A2A28]/50">
                <ArrowDown size={14} className="animate-bounce" />
                <span>Scrollen – Entdecken</span>
              </div>
            </div>

            <div className="md:col-span-6 relative">
              <div className="relative w-full aspect-[4/5] max-h-[72vh]">
                <ImageReveal src={home.heroImage} alt="Luxuriöse Immobilien" className="w-full h-full" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A2A28]/15 via-transparent to-transparent pointer-events-none" />
                <div className="absolute -left-4 -bottom-4 md:-left-8 md:-bottom-8 bg-[#0A2A28] text-[#E8E2D5] px-6 py-4 shadow-2xl">
                  <div className="font-mono-up text-[#BFA070] mb-1">Seit 2000</div>
                  <div className="font-display text-xl">450+ Einheiten betreut</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FOCUS section */}
      <section className="section">
        <div className="container-x grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div
              className="lux-image-wrap"
              style={{ transform: `translateY(${Math.max(-40, (scrollY - 800) * -0.06)}px)` }}
            >
              <ImageReveal
                src="https://images.pexels.com/photos/13608798/pexels-photo-13608798.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Unser Fokus"
                className="aspect-[4/5]"
              />
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <ScrollReveal>
              <div className="font-mono-up text-[#0A2A28]/60 mb-6">
                <span className="gold-line" />Unser Fokus
              </div>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.96] text-[#0A2A28] mb-8">
                Werte, die <span className="font-serif-italic text-[#BFA070]">wachsen</span> sollen,
                brauchen Weitsicht.
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <p className="text-[#0A2A28]/75 text-lg leading-relaxed">{home.focus.text}</p>
            </ScrollReveal>
            <ScrollReveal delay={3} className="mt-10">
              <Link to="/objekte" className="lux-btn">
                <span>Mehr erfahren</span>
                <ArrowUpRight className="arrow" size={16} strokeWidth={1.5} />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pillars / Dark value section */}
      <section className="section dark-section radial-glow">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-20">
            <div className="md:col-span-5">
              <ScrollReveal>
                <div className="font-mono-up text-[#BFA070] mb-6">
                  <span className="gold-line" />Unser Anspruch
                </div>
              </ScrollReveal>
              <ScrollReveal delay={1}>
                <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
                  Immobilien verwalten – <br />
                  <span className="font-serif-italic text-[#BFA070]">Werte betreuen.</span>
                </h2>
              </ScrollReveal>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <ScrollReveal delay={2}>
                <p className="text-[#E8E2D5]/80 text-lg leading-relaxed">{home.pillarsText}</p>
              </ScrollReveal>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {home.pillars.map((p, i) => (
              <ScrollReveal key={p} delay={i + 1}>
                <div
                  className="animated-box h-full"
                  style={{ '--lux-delay': `${i * -1.3}s` }}
                >
                  <span className="animated-box-border" />
                  <span className="animated-box-bg" />
                  <div className="relative z-[2] p-10 h-full group">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 size={28} strokeWidth={1.25} className="text-[#BFA070] flex-shrink-0 mt-1" />
                      <div>
                        <div className="lux-num mb-3">0{i + 1}</div>
                        <h3 className="font-display text-3xl leading-tight text-[#E8E2D5] group-hover:text-[#BFA070] transition-colors">
                          {p}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Stats - sequential count-up from left to right */}
          <StatsRow stats={home.stats} />
        </div>
      </section>

      {/* Services preview */}
      <section className="section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <ScrollReveal>
                <div className="font-mono-up text-[#0A2A28]/60 mb-6">
                  <span className="gold-line" />Unsere Leistungen
                </div>
              </ScrollReveal>
              <ScrollReveal delay={1}>
                <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#0A2A28]">
                  Ein Fundament aus <br />
                  <span className="font-serif-italic text-[#BFA070]">Klarheit.</span>
                </h2>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={2}>
              <Link to="/leistungen" className="lux-btn">
                <span>Alle Leistungen</span>
                <ArrowUpRight className="arrow" size={16} strokeWidth={1.5} />
              </Link>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.list.slice(0, 3).map((s, i) => (
              <ScrollReveal key={s.id} delay={i + 1}>
                <Link to={`/leistungen#${s.id}`} className="group block lux-card">
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A2A28]/70 via-transparent to-transparent" />
                    <div className="absolute top-6 left-6 font-mono-up text-[#BFA070]">{s.number}</div>
                    <div className="absolute bottom-8 left-8 right-8 text-[#F5F1EA]">
                      <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">{s.title}</h3>
                      <div className="flex items-center gap-2 font-mono-up text-[11px] text-[#BFA070] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <span>Mehr erfahren</span>
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <ContactBlock />
    </>
  );
};

// Stats with sequential count-up animation
const StatsRow = ({ stats }) => {
  // Only trigger when the row is clearly on screen: require 50% visibility and
  // a 20% bottom rootMargin so the counter doesn't start until the user has
  // actually scrolled to the block.
  const [ref, inView] = useInView({ threshold: 0.5, rootMargin: '0px 0px -20% 0px' });
  // Stagger each counter so they animate one after another from left to right.
  // Each counter runs ~1600ms; start next 1100ms later → visible sequence but lively pace.
  const stepDelay = 1100;

  return (
    <div ref={ref} className="mt-24 md:mt-28">
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-[#E8E2D5]/15">
        {stats.map((s, i) => {
          const borderCls =
            i === 0
              ? ''
              : i === 1
              ? 'border-l border-[#E8E2D5]/15'
              : i === 2
              ? 'border-t border-[#E8E2D5]/15 md:border-t-0 md:border-l'
              : 'border-l border-t border-[#E8E2D5]/15 md:border-t-0';
          return (
            <div key={s.label} className={`relative py-12 md:py-14 px-6 md:px-8 ${borderCls}`}>
            {/* gold index marker */}
            <div className="font-mono-up text-[#BFA070]/70 text-[10px] mb-5">0{i + 1}</div>

            <div
              className="font-display font-light text-[#BFA070] leading-[0.9] mb-4 tabular-nums text-5xl md:text-6xl lg:text-7xl"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 700ms ease ${i * stepDelay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * stepDelay}ms`,
              }}
            >
              <CountUp
                value={s.value}
                start={inView}
                duration={1600}
                startDelay={i * stepDelay}
              />
            </div>

            <div
              className="font-mono-up text-[#E8E2D5]/70"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(14px)',
                transition: `opacity 600ms ease ${i * stepDelay + 250}ms, transform 700ms ease ${i * stepDelay + 250}ms`,
              }}
            >
              {s.label}
            </div>

            {/* thin gold underline progression indicator */}
            <div
              className="absolute bottom-0 left-0 h-[2px] bg-[#BFA070]"
              style={{
                width: inView ? '100%' : '0%',
                transition: `width 1500ms cubic-bezier(0.22,1,0.36,1) ${i * stepDelay}ms`,
              }}
            />
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default Home;
